<template>
  <div
    class="relative mb-4"
    @selectstart="onSelectStart"
    :style="{ height: height + 'px' }"
    @scroll="onScroll"
  >
    <!-- This element is used to calculate the item size and subsequently the grid layout -->
    <candidate-placeholder
      class="mr-1 mb-1"
      style="visibility: hidden;position: absolute; pointer-events: none;"
      :small="candidateSize === 'small'"
    ></candidate-placeholder>
    <!-- Placeholders -->
    <div
      v-for="(entry, index) in entries"
      :key="'placeholder-' + index"
      class="candidate-slot-placeholder relative"
      :style="{
        transform: 'translate(' + entry.x + 'px, ' + entry.y + 'px)'
      }"
    >
      <router-link
        v-if="
          showAddCandidateButton &&
            parseInt(index) === entries.findIndex(c => c.candidateId === null)
        "
        :to="{ name: 'match' }"
        draggable="false"
      >
        <candidate-placeholder
          :interactable="true"
          :small="candidateSize === 'small'"
        ></candidate-placeholder>
      </router-link>
      <candidate-placeholder
        v-else-if="entry.candidateId == null || dropMode === 'replace'"
        :interactable="dropMode === 'replace'"
        :small="candidateSize === 'small'"
        @click="onEmptySlotInteract"
      ></candidate-placeholder>
    </div>
    <!-- Candidate Entries -->
    <div
      v-for="(entry, index) in entries"
      :key="entry.uid"
      style="overflow: visible;"
      class="candidate-slot relative"
      :style="{
        transform: 'translate(' + entry.x + 'px, ' + entry.y + 'px)'
      }"
      :class="{
        'candidate-position-animated': enableAnimations,
        'pointer-events-none': draggingCandidate
      }"
      draggable="false"
    >
      <div
        v-if="entry.candidateId > 0 && incrementalLoadingStep >= index"
        draggable="false"
        @click="onCandidateClick({ candidateId: entry.candidateId })"
        :id="getCandidateDivId(entry.candidateId)"
        :class="{ 'pointer-events-none': draggingCandidate }"
      >
        <candidate-icon
          :uid="entry.uid"
          :candidate-id="entry.candidateId"
          :active-list="activeList"
          :show-name="true"
          :show-match-percent="true"
          :show-position="true"
          :truncate-name="truncateName"
          :position="entry.rank != null ? entry.rank : 0"
          :candidateDraggable="dragMode != null"
          :clone-on-drag="dragMode === 'clone'"
          :small="candidateSize === 'small'"
          @candidatepointerdown="onCandidatePointerDown"
          @candidatedragstart="onCandidateDragStart"
        ></candidate-icon>
      </div>
    </div>
  </div>
</template>

<script>
import CandidateIcon from "@/components/people/CandidateIcon";
import CandidatePlaceholder from "@/components/people/CandidatePlaceholder";
import Vue from "vue";
import { ViewportHelper } from "@/utils/ViewportHelper";
import { mapGetters } from "vuex";
import { MatchType } from "@/utils/MatchType";
import constants from "@/constants";

/**
 * @type CandidateSlotGrid
 * Arranges a collection of candidates in a grid that, depending on the settings, can be interacted with in various
 * ways.
 * Events:
 * dragstart: Triggered when the user starts dragging a candidate
 * candidatedrop: Triggered when the user drops a candidate on the grid
 * placeholderclick: Triggered when the user interacts with a placeholder
 */
export default {
  name: "CandidateSlotGrid",
  components: { CandidateIcon, CandidatePlaceholder },
  props: {
    /**
     * @var The candidate ids in their correct order, with null for empty slots
     */
    candidateIds: {
      type: Array,
      required: false
    },
    /**
     * @var Drag bus for inter component communication of drag and drop actions
     */
    candidateDragBus: {
      type: Vue,
      default: null
    },
    /**
     * @var One of: null|"clone"|"drag"
     */
    dragMode: {
      type: String,
      default: null
    },
    /**
     * @var One of: null|"insert"|"replace"
     */
    dropMode: {
      type: String,
      default: null
    },
    /**
     * @var Used to determine which drop target will be interacted with when the drag is released. A higher value
     * means a higher priority.
     */
    dragInteractionPriority: {
      type: Number,
      default: 0
    },
    /**
     * @description If assigned, clamps the area where interactions are possible. E.g. to prevent drops outside of the
     * viewport of a surrounding scroll area.
     * @template [top, right, bottom, left]
     */
    dropBounds: {
      type: Array,
      default: null
    },
    /**
     * @var Whether to show the plus button
     */
    showAddCandidateButton: {
      type: Boolean,
      default: false
    },
    truncateName: {
      type: Boolean,
      default: false
    },
    allowDropOnEmptySlot: {
      type: Boolean,
      default: false
    },
    /**
     * @var One of: "normal"|"small"
     */
    candidateSize: {
      type: String,
      default: "normal"
    },
    enableAnimations: {
      type: Boolean,
      default: true
    },
    /**
     * @var Provides context for which list the candidates are looking at to check their seat count. Takes the dream
     * team if null.
     */
    activeList: {
      type: Object,
      default: null
    },
    listId: {
      type: Number,
      required: false
    }
  },
  data: () => {
    return {
      entries: [],
      gridArea: null,
      editorAreaWidth: 0,
      entryWidth: 0,
      entryHeight: 0,
      entrySpacingX: 0,
      entrySpacingY: 0,
      columns: 0, // amount of columns, needed to calculate drag interactions and the layout
      rows: 0, // amount of rows, needed to calculate drag interactions
      height: 0,
      contentOffsetLeft: 0, // slot grid is centered, this value represents the left offset of the grid
      draggingCandidate: false,
      draggedCandidateIndex: -1,
      lastActionIsDrag: false,
      elementRef: constants.PORTRAIT_ID,
      incrementalLoadingStep: 0
    };
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
    if (this.candidateDragBus) {
      this.candidateDragBus.$on("dragstart", this.onCandidateDragStartForeign); // listen to drag start events from other areas
      this.candidateDragBus.$on("drag", this.onCandidateDrag); // listen to drag events to perform drag interactions
      this.candidateDragBus.$on("dragend", this.onCandidateDragEnd); // listen to drag end events to suggest drop interactions
      this.candidateDragBus.$on("drop", this.onCandidateDrop); // listen to drop events to perform drop interactions
    }
  },
  async mounted() {
    this.gridArea = this.$el;
    this.recalculateEntrySize();
    this.entries = [];
    this.updateEntries();
    this.onWindowResize();

    if (this.candidateIds != null) {
      await this.incrementalLoad();

      this.$nextTick(() => {
        this.$emit("mountedCandidateSlotGrid", { listId: this.listId });
      });
    }
  },
  destroyed() {
    window.removeEventListener("resize", this.onWindowResize);
    if (this.candidateDragBus) {
      this.candidateDragBus.$off("dragstart", this.onCandidateDragStartForeign);
      this.candidateDragBus.$off("drag", this.onCandidateDrag);
      this.candidateDragBus.$off("dragend", this.onCandidateDragEnd);
      this.candidateDragBus.$off("drop", this.onCandidateDrop);
    }
  },
  computed: {
    ...mapGetters("candidate", ["matches"]),
    ...mapGetters("election", ["activeElection"])
  },
  watch: {
    async candidateIds() {
      this.incrementalLoadingStep = 0;
      const previousRows = this.rows;
      this.updateEntries();
      if (previousRows <= 1) {
        this.onWindowResize();
      }
      await this.incrementalLoad();
      this.$nextTick(() => {
        this.$emit("mountedCandidateSlotGrid", { listId: this.listId });
      });
    }
  },
  methods: {
    onWindowResize() {
      this.recalculateEntrySize();
      const grid = [...this.gridArea.children];
      if (grid.length === 0) {
        this.columns = 0;
        this.updateLayout();
        return;
      }
      const areaWidth = parseInt(
        getComputedStyle(this.gridArea).width.replace("px", "")
      );
      this.editorAreaWidth = areaWidth;
      this.columns = Math.min(
        this.entries.length,
        Math.floor(
          (areaWidth + this.entrySpacingX) /
            (this.entryWidth + this.entrySpacingX)
        )
      );
      this.updateLayout();
    },
    onScroll(event) {
      this.$emit("scroll", event);
    },
    async incrementalLoad() {
      while (this.incrementalLoadingStep < this.entries.length) {
        this.incrementalLoadingStep += 100;
        await new Promise(fulfill => setTimeout(fulfill, 10));
        this.updateLayout();
      }
    },
    recalculateEntrySize() {
      const computedTemplateStyle = getComputedStyle(this.gridArea.children[0]);
      this.entryWidth = parseInt(computedTemplateStyle.width.replace("px", ""));
      this.entryHeight = parseInt(
        computedTemplateStyle.height.replace("px", "")
      );
      this.entrySpacingX = parseInt(
        computedTemplateStyle.marginRight.replace("px", "")
      );
      this.entrySpacingY = parseInt(
        computedTemplateStyle.marginBottom.replace("px", "")
      );
    },
    onSelectStart(event) {
      event.stopPropagation();
      event.preventDefault();
    },
    onCandidateClick(event) {
      if (this.lastActionIsDrag) {
        return;
      }
      this.$store.commit("list/scrollTo", {
        candidateId: event.candidateId,
        listId: this.listId
      });
      this.$router.push({
        name: "candidate",
        params: {
          candidateId: event.candidateId,
          electionId: this.activeElection.id
        }
      });
    },
    onCandidatePointerDown() {
      this.lastActionIsDrag = false;
    },
    onCandidateDragStart(event) {
      event.source = this;
      this.candidateDragBus.$emit("dragstart", event);
      this.draggingCandidate = true;
      this.lastActionIsDrag = true;
      this.draggedCandidateIndex = this.entries.findIndex(
        e => e.uid === event.uid
      );
    },
    onCandidateDragStartForeign(event) {
      if (event.source === this) {
        return;
      }
      this.draggingCandidate = true;
      this.lastActionIsDrag = true;
      this.draggedCandidateIndex = -1;
    },
    onCandidateDrag(event) {
      switch (this.dropMode) {
        case "insert":
          this.handleCandidateInsert(event);
          break;
        case "replace":
          // this.handleCandidateReplace(event);
          break;
        default:
          break;
      }
    },
    onCandidateDragEnd(event) {
      if (this.dropMode != null) {
        event.targets.push({
          target: this,
          name: "candidate-slot-grid",
          priority: this.dragInteractionPriority
        });
      }

      setTimeout(() => {
        this.draggingCandidate = false;
      }, 5);
    },
    onCandidateDrop(event) {
      switch (this.dropMode) {
        case "insert":
          if (event.target !== this) {
            return;
          }
          this.$emit("candidatedrop", {
            candidateId: event.candidateId,
            index: this.draggedCandidateIndex
          });
          break;
        case "replace": {
          const draggedIndex = this.draggedCandidateIndex;
          const dropInfo = this.handleCandidateReplace(event);
          const dropIndex = dropInfo.index;
          const dropEvent = {
            candidateId: event.candidateId,
            index: dropIndex >= 0 ? dropIndex : null,
            oldIndex: draggedIndex >= 0 ? draggedIndex : null,
            cancelled: false
          };
          this.$emit("candidatedrop", dropEvent);
          if (dropEvent.cancelled) {
            this.undoCandidateReplace(dropEvent, dropInfo);
          }
          break;
        }
        default:
          break;
      }
    },
    onEmptySlotInteract(event) {
      this.$emit("placeholderclick", event);
    },
    handleCandidateInsert(event) {
      const index = this.getEntryIndex(
        event.x,
        event.y,
        event.clientX,
        event.clientY
      );
      const oldIndex = this.draggedCandidateIndex;
      if (
        index === oldIndex ||
        index < 0 ||
        index >= this.candidateIds.length ||
        (!this.allowDropOnEmptySlot && this.candidateIds[index] == null)
      ) {
        return;
      }

      const entry = this.getEntryAt(oldIndex);
      this.entries.splice(oldIndex, 1);
      this.entries.splice(index, 0, entry);
      entry.index = index;
      for (let i = 0; i < this.entries.length; i++) {
        this.entries[i].position = i;
        this.entries[i].rank = i + 1;
      }
      this.draggedCandidateIndex = index;
      setTimeout(() => {
        this.updateLayout();
      }, 50);
    },
    handleCandidateReplace(event) {
      const index =
        event.target === this
          ? this.getEntryIndex(event.x, event.y, event.clientX, event.clientY)
          : -1;
      const oldCandidateId =
        index >= 0 && index < this.candidateIds.length
          ? this.candidateIds[index]
          : null;
      const oldIndex = this.draggedCandidateIndex;
      const draggedFromOutside = this.draggedCandidateIndex < 0;
      const droppedOutside =
        index < 0 ||
        index >= this.candidateIds.length ||
        (!this.allowDropOnEmptySlot && this.candidateIds[index] == null);
      if (draggedFromOutside && droppedOutside) {
        return;
      }

      const entryA = oldIndex >= 0 ? this.getEntryAt(oldIndex) : null;
      const entryB = index >= 0 ? this.getEntryAt(index) : null;
      const candidateA = event.candidateId;
      const rankA =
        candidateA != null && this.matches[candidateA] != null
          ? this.matches[candidateA].rank
          : null;
      const candidateB = entryB ? entryB.candidateId : null;
      const rankB = entryB ? entryB.rank : null;
      if (entryA && entryB && this.enableAnimations) {
        this.entries[index] = this.createEntry();
        this.entries[index].candidateId = candidateA;
        this.entries[index].rank = rankA;
        this.entries[index].x = entryB.x;
        this.entries[index].y = entryB.y;
        this.entries[oldIndex] = entryB;
      } else {
        if (entryA) {
          entryA.candidateId = candidateB;
          entryA.rank = rankB;
        }
        if (entryB) {
          entryB.candidateId = candidateA;
          entryB.rank = rankA;
        }
      }
      for (let i = 0; i < this.entries.length; i++) {
        this.entries[i].position = i;
      }
      if (this.enableAnimations) {
        setTimeout(() => {
          this.updateLayout();
        }, 50);
      } else {
        this.updateLayout();
      }

      return { index, oldCandidateId };
    },
    undoCandidateReplace(event, dropInfo) {
      let changed = false;
      if (
        event.index != null &&
        event.index >= 0 &&
        event.index < this.candidateIds.length
      ) {
        this.entries[event.index].candidateId = dropInfo.oldCandidateId;
        changed = true;
      }
      if (
        this.draggedCandidateIndex != null &&
        this.draggedCandidateIndex >= 0
      ) {
        this.entries[this.draggedCandidateIndex].candidateId =
          event.candidateId;
        changed = true;
      }
      if (changed) {
        this.updateEntries();
      }
      if (!this.enableAnimations) {
        this.updateLayout();
      }
    },
    updateEntries() {
      const candidateIds = this.candidateIds || [];
      const desiredEntriesCount =
        candidateIds.length +
        (this.showAddCandidateButton &&
        candidateIds.findIndex(candidateId => candidateId === null) < 0
          ? 1
          : 0);
      if (this.entries.length !== desiredEntriesCount) {
        this.entries.length = desiredEntriesCount;
      }
      for (let i = 0; i < this.entries.length; i++) {
        if (this.entries[i] != null) {
          this.entries[i].candidateId = null;
          this.entries[i].position = null;
          this.entries[i].rank = null;
          continue;
        }
        this.entries[i] = this.createEntry();
      }

      for (let i = 0; i < candidateIds.length; i++) {
        const candidateId = candidateIds[i];
        const match = this.matches[candidateId];
        const index = i;
        this.entries[index].candidateId = candidateId;
        this.entries[index].rank =
          match && match.type !== MatchType.Dislike ? match.rank : null;
        this.entries[index].position = i;
      }

      if (this.filtersActive) {
        this.entries.sort((a, b) => {
          const indexA = candidateIds.indexOf(a.candidateId);
          const indexB = candidateIds.indexOf(b.candidateId);
          return indexA > indexB ? 1 : -1;
        });
      }
    },
    updateLayout() {
      const entryWidth = this.entryWidth;
      const entryHeight = this.entryHeight;
      const entrySpacingX = this.entrySpacingX;
      const entrySpacingY = this.entrySpacingY;
      const columns = this.columns;
      const rows = columns > 0 ? Math.ceil(this.entries.length / columns) : 0;
      const contentWidth = Math.max(
        0,
        columns * entryWidth + (columns - 1) * entrySpacingX
      );
      const contentOffsetLeft = (this.editorAreaWidth - contentWidth) / 2;

      this.rows = rows;
      this.height = rows * this.entryHeight + (rows - 1) * this.entrySpacingY;
      this.contentOffsetLeft = contentOffsetLeft;
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < columns; x++) {
          const index = x + y * columns;
          if (index >= this.entries.length) {
            break;
          }

          const entry = this.getEntryAt(index);
          entry.x = contentOffsetLeft + x * entryWidth + x * entrySpacingX;
          entry.y = y * entryHeight + y * entrySpacingY;
        }
      }
      this.$forceUpdate();
    },
    createEntry() {
      return {
        uid: this.$uuid.v4(),
        candidateId: null,
        position: null,
        rank: null,
        x: 0,
        y: 0
      };
    },
    getEntryAt(index) {
      return this.entries[index];
    },
    getEntryIndex(x, y, clientX, clientY) {
      if (this.dropBounds) {
        const top = this.dropBounds[0];
        const right = this.dropBounds[1];
        const bottom = this.dropBounds[2];
        const left = this.dropBounds[3];
        if (
          clientX < left ||
          clientY < top ||
          clientX > right ||
          clientY > bottom
        ) {
          return -1;
        }
      }
      const gridArea = this.gridArea;
      const editorAreaPosition = ViewportHelper.getElementViewportPosition(
        gridArea
      );
      const entryWidth = this.entryWidth;
      const entryHeight = this.entryHeight;
      const entrySpacingX = this.entrySpacingX;
      const entrySpacingY = this.entrySpacingY;
      const posX = editorAreaPosition.x + this.contentOffsetLeft;
      const posY = editorAreaPosition.y;
      const relativePosX = x - posX;
      const relativePosY = y - posY;

      const slotX = Math.floor(
        (relativePosX + (entryWidth + entrySpacingX) / 2) /
          (entryWidth + entrySpacingX)
      );
      const slotY = Math.floor(
        (relativePosY + (entryHeight + entrySpacingY) / 2) /
          (entryHeight + entrySpacingY)
      );
      return slotX >= 0 &&
        slotX < this.columns &&
        slotY >= 0 &&
        slotY < this.rows
        ? slotX + slotY * this.columns
        : -1;
    },
    getCandidateDivId(candidateId) {
      const listRef = this.listId ? `-${this.listId}` : "";
      return `${this.elementRef}${candidateId}${listRef}`;
    }
  }
};
</script>

<style scoped>
.candidate-slot,
.candidate-slot-placeholder {
  position: absolute;
  top: 0;
  left: 0;
}

.candidate-position-animated {
  transition: transform 0.2s ease-out;
}
</style>
